import axios from 'axios';

import { Id, getData } from '@feature/utils';

import {
  GovAuthorEvaluation,
  GovEvalAuthorAnnexRequest,
  GovEvalHIndexRequest,
  GovEvalMinimalStandardRequest,
  GovEvalUserDataRequest,
  GovEvaluation,
  GovStudyDomain,
  HIndexType,
} from './GovEvaluationTypes';

export function getGovEvaluationHIndexDownloadUrl(evaluationId: Id, authorId: Id, type: HIndexType) {
  return `/api/gov-evaluation/${evaluationId}/author/${authorId}/download-h-index-proof?type=${type}`;
}

export function getGovEvaluationSignedAnnexDownloadUrl(evaluationId: Id, authorId: Id, type: string) {
  return `/api/gov-evaluation/${evaluationId}/author/${authorId}/download-annex-signed-proof?type=${type}`;
}

export function getGovEvaluationEditableAnnexDownloadUrl(evaluationId: Id, authorId: Id, type: string) {
  return `/api/gov-evaluation/${evaluationId}/author/${authorId}/download-annex-editable-proof?type=${type}`;
}

export function getGovEvaluationAutomatedAnnexDownloadUrl(evaluationId: Id, authorId: Id, type: string) {
  return `/api/gov-evaluation/${evaluationId}/author/${authorId}/download-automated-annex?type=${type}`;
}

export function updateHIndex(evaluationId: Id, request: GovEvalHIndexRequest) {
  const formData = new FormData();
  formData.append('type', request.type);
  formData.append('hIndex', String(request.hIndex));
  formData.append('file', request.file);
  return axios
    .put<unknown>(`/gov-evaluation/${evaluationId}/author/upload-h-index`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(getData);
}

export function deleteMinimalStandards(evaluationId: Id) {
  return axios.delete<unknown>(`/gov-evaluation/${evaluationId}/author/minimal-standard`).then(getData);
}

export function updateMinimalStandard(evaluationId: Id, request: GovEvalMinimalStandardRequest) {
  const formData = new FormData();
  formData.append(
    'payload',
    new Blob(
      [
        JSON.stringify({
          ...request,
          proofFile: undefined,
          editableProofFile: undefined,
        }),
      ],
      { type: 'application/json' },
    ),
  );
  formData.append('proofFile', request.proofFile);
  formData.append('editableProofFile', request.editableProofFile);
  return axios
    .put<unknown>(`/gov-evaluation/${evaluationId}/author/minimal-standard`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(getData);
}

export function updateAnnex(evaluationId: Id, request: GovEvalAuthorAnnexRequest) {
  const formData = new FormData();
  formData.append('type', request.type);
  formData.append('signedFile', request.signedFile);
  formData.append('editableFile', request.editableFile);
  return axios
    .put<unknown>(`/gov-evaluation/${evaluationId}/author/upload-annex`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(getData);
}

export function getStudyDomains(evaluationId: Id) {
  return axios.get<GovStudyDomain[]>(`/gov-evaluation/${evaluationId}/study-domain`).then(getData);
}

export function getAll() {
  return axios.get<GovEvaluation[]>(`/gov-evaluation`).then(getData);
}

export function get(evaluationId: Id) {
  return axios.get<GovEvaluation>(`/gov-evaluation/${evaluationId}`).then(getData);
}

export function open(year: number) {
  return axios.post<unknown>(`/gov-evaluation/${year}/open`).then(getData);
}

export function start(evaluationId: Id) {
  return axios.put<unknown>(`/gov-evaluation/${evaluationId}/author/start`).then(getData);
}

export function updateUserData(evaluationId: Id, request: GovEvalUserDataRequest) {
  return axios.put<unknown>(`/gov-evaluation/${evaluationId}/author/user-data`, request).then(getData);
}

export function complete(evaluationId: Id) {
  return axios.put<unknown>(`/gov-evaluation/${evaluationId}/author/complete`).then(getData);
}

export function getForAuthor(evaluationId: Id, authorId: Id) {
  return axios
    .get<GovAuthorEvaluation | null>(`/gov-evaluation/${evaluationId}/author/${authorId}`)
    .then(getData)
    .then(data => (!data ? null : data));
}
